'use client';

import { FC, useContext } from 'react';
import {
  handleToolTipOnOver,
  handleToolTipMouseOut,
} from '../shared/ow-tool-tip';
import SpriteIcon from '../shared/sprite-icon';
import { useTranslation } from '../../hooks/use-translation';
import AppPageContext from '../../context/app-page-context';
import Utils from '../../utils/utils';
import { AppDownload } from '../../interfaces/domain/ow-app.modal';

// -----------------------------------------------------------------------------
const clientConfig = {
  overwolf: {
    labelKey: 'appInfoOverwolfClient',
    icon: '/static_next/img/client-icons/platform-icon.svg',
  },
  electron: {
    labelKey: 'appInfoElectron',
    icon: '/static_next/img/client-icons/electron-icon.svg',
  },
};

// -----------------------------------------------------------------------------
interface TagItem {
  className: string;
  tooltipKey?: string;
  icon?: string;
  imgSrc?: string;
  imgAlt?: string;
  label: string;
}

// -----------------------------------------------------------------------------
interface AppRichDetailsInfoProps {
  lightView?: boolean;
}

// -----------------------------------------------------------------------------
export const AppRichDetailsInfo: FC<AppRichDetailsInfoProps> = ({
  lightView = false,
}): JSX.Element => {
  const { t } = useTranslation();
  const { app } = useContext(AppPageContext);

  const downloads = app?.alternativeDownloads;

  if (!downloads || downloads.length === 0) {
    return <></>;
  }

  // Group downloads by client type
  const overwolfDownloads = downloads.filter((d) => !d.isElectron);
  const electronDownloads = downloads.filter((d) => d.isElectron);

  // Build tag items from a download entry — missing data is simply omitted
  const buildTags = (
    download: AppDownload,
    clientType: 'overwolf' | 'electron',
  ): TagItem[] => {
    const config = clientConfig[clientType];
    const label = t(config.labelKey);
    const tags: TagItem[] = [];

    if (download.sizeInBytes) {
      tags.push({
        className: 'file-size',
        imgSrc: config.icon,
        imgAlt: label,
        label: `${label} (${Utils.formatBytes(download.sizeInBytes)})`,
      });
    }

    // In lightView mode, only show the platform + file size tag
    if (!lightView) {
      if (download.latestVersion) {
        tags.push({
          className: 'version',
          tooltipKey: 'appInfoVersion',
          icon: 'appInfoVersion',
          label: download.latestVersion,
        });
      }

      if (download.updatedAt) {
        tags.push({
          className: 'last-update',
          tooltipKey: 'appInfoLastUpdate',
          icon: 'appInfoLastUpdate',
          label: Utils.formatDate(download.updatedAt),
        });
      }
    }

    return tags;
  };

  // Collect client groups to render (only those with at least one detail field)
  const clientGroups: { clientType: 'overwolf' | 'electron'; download: AppDownload }[] = [];

  if (overwolfDownloads.length > 0) {
    clientGroups.push({ clientType: 'overwolf', download: overwolfDownloads[0] });
  }
  if (electronDownloads.length > 0) {
    clientGroups.push({ clientType: 'electron', download: electronDownloads[0] });
  }

  // If none of the downloads have any detail fields, don't render the section
  const allTags = clientGroups.map((g) => buildTags(g.download, g.clientType));
  if (allTags.every((tags) => tags.length === 0)) {
    return <></>;
  }

  const containerClass = lightView
    ? 'app-rich-details-info light-view'
    : 'app-rich-details-info';

  return (
    <div className={containerClass}>
      {!lightView && <h4 className='tag-group-title'>{t('appInfo')}</h4>}

      {clientGroups.map(({ clientType }, idx) => {
        const tags = allTags[idx];
        if (tags.length === 0) return null;

        return (
          <ul className='tag-list' key={clientType}>
            {tags.map((tag) => (
              <li
                key={tag.className}
                className={`tag-item ${tag.className}`}
                {...(tag.tooltipKey ? {
                  'data-tooltip': t(tag.tooltipKey),
                  'tooltip-position': 'bottom',
                  onMouseOver: handleToolTipOnOver,
                  onMouseOut: handleToolTipMouseOut,
                } : {})}
              >
                {tag.imgSrc && <img src={tag.imgSrc} alt={tag.imgAlt} />}
                {tag.icon && <SpriteIcon icon={tag.icon} />}
                <span>{tag.label}</span>
              </li>
            ))}
          </ul>
        );
      })}
    </div>
  );
};
